import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, User, Loader, CheckCircle, ArrowLeft, Stethoscope, Crown, ChevronDown } from 'lucide-react';
import Swal from 'sweetalert2';
import { createIncludedConsultation } from '../services/consultationService';
import { isSlotBookable, MIN_BOOKING_LEAD_HOURS } from '../services/scheduleModels';
import { fetchConsultationReasons } from '../services/FirebaseService';
import WhatsappVerificationModal from './WhatsappVerificationModal';

/**
 * IncludedConsultationConfirm — Collects reason and confirms an included consultation.
 *
 * Props:
 *   - selectedSlot     (object)  – the selected schedule slot
 *   - selectedDate     (Date)    – date of appointment
 *   - selectedTime     (string)  – time string
 *   - specialtyConfig  (object)  – map of specialty configs
 *   - selectedSpecialty(number)  – specialty id
 *   - loggedInUser     (object)  – { daisUserId, authToken, firebaseUid, ... }
 *   - campaignDurationMap (object) – duration mappings
 *   - allSpecialties   (array)   – full specialties array for fallback lookup
 *   - onComplete       (fn)      – called with the result on success
 *   - onBack           (fn)      – navigate back
 */
const IncludedConsultationConfirm = ({
  selectedSlot,
  selectedDate,
  selectedTime,
  specialtyConfig,
  selectedSpecialty,
  loggedInUser,
  campaignDurationMap,
  allSpecialties = [],
  onComplete,
  onBack
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consultationReasons, setConsultationReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState(null);
  const [loadingReasons, setLoadingReasons] = useState(false);

  // Gate de verificación de teléfono (V2 — réplica de runBookingWithAccountGate).
  // Si el backend devuelve PHONE_NOT_VERIFIED, abrimos el modal de WhatsApp
  // y al terminar reintentamos el booking automáticamente.
  const [showWhatsappGate, setShowWhatsappGate] = useState(false);
  const pendingBookingRef = useRef(null);

  // ─── Resolve specialty config (same self-sufficient pattern as DoctorSelection) ───
  const specConfig = (() => {
    // Try from specialtyConfig prop first
    const fromConfig = specialtyConfig?.[selectedSpecialty];
    if (fromConfig && fromConfig.id) return fromConfig;

    // Fallback: look up from allSpecialties
    const specId = parseInt(selectedSpecialty) || selectedSlot?.specialty_id;
    if (!specId) return {};

    const fromList = allSpecialties.find(s => s.id === specId || s.odooId === specId);
    if (fromList) {
      return {
        id: fromList.odooId || fromList.id,
        duration: fromList.durationId,
        extraDuration: fromList.extraDurationId,
        name: fromList.name,
        icon: fromList.icon,
      };
    }

    return { id: specId };
  })();

  const specialtyId = specConfig.id || parseInt(selectedSpecialty) || null;
  const specialtyName = specConfig.name || 'Consulta';

  // Resolve duration_id: from slot, campaign, or specialty config
  const durationId = selectedSlot?.duration_id ||
    campaignDurationMap?.[specialtyId]?.includedDurationId ||
    specConfig.duration ||
    null;

  const formattedDate = selectedDate
    ? selectedDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';

  const doctorName = selectedSlot?.professional_name || selectedSlot?.professional_ids?.[0]?.name || 'Doctora';

  // Slot "bajo confirmación": la doctora debe aceptar la solicitud. No requiere
  // membresía; el backend la crea como pending_doctor_confirmation sin cobrar.
  const isOnDemand = selectedSlot?.isOnDemand === true || selectedSlot?.bookingMode === 'on_demand';

  // ─── Fetch consultation reasons from Firebase ───
  useEffect(() => {
    if (!specialtyId) {
      console.warn('[IncludedConsultationConfirm] No specialtyId, skipping reason fetch');
      return;
    }

    const loadReasons = async () => {
      setLoadingReasons(true);
      try {
        console.log('[IncludedConsultationConfirm] Fetching reasons for specialty:', specialtyId);
        const reasons = await fetchConsultationReasons(specialtyId);
        console.log('[IncludedConsultationConfirm] Loaded reasons:', reasons.length, reasons.map(r => ({id: r.id, name: r.name})));
        setConsultationReasons(reasons);
      } catch (err) {
        console.error('[IncludedConsultationConfirm] Error loading reasons:', err);
      } finally {
        setLoadingReasons(false);
      }
    };
    loadReasons();
  }, [specialtyId]);

  // Ejecuta la llamada de booking. Si el backend devuelve PHONE_NOT_VERIFIED,
  // guarda el contexto y devuelve `gated: true` para que el caller abra el
  // modal de WhatsApp y reintente automáticamente tras verificar.
  const runBooking = async () => {
    const authToken = loggedInUser?.authToken;
    const userId = loggedInUser?.daisUserId || loggedInUser?.userId;
    const doctorId = selectedSlot?.professional_ids?.[0]?.id || null;

    return createIncludedConsultation(authToken, {
      doctor_id: doctorId,
      user_id: userId,
      date: selectedDate.toISOString().split('T')[0],
      duration_id: durationId,
      schedule_id: selectedSlot?.id,
      reason: reason.trim(),
      reminder: true,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
      parent_id: null,
      consultation_reason_id: selectedReasonId || null,
      specialty_id: specialtyId
    });
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Motivo de consulta',
        text: 'Por favor describe brevemente el motivo de tu consulta.',
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    // Guard de anticipación: nunca enviamos la solicitud (ni se dispara el
    // WhatsApp del backend) si el horario ya no cumple el mínimo. Defensa en
    // profundidad: el selector ya filtra estos slots, pero el usuario pudo
    // tardar en la pantalla y cruzar el umbral.
    const slotDate = selectedSlot?.date || (selectedDate
      ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
      : '');
    const slotTime = selectedSlot?.hourStart || selectedSlot?.init || selectedTime || '';
    if (!isSlotBookable(slotDate, slotTime)) {
      Swal.fire({
        icon: 'warning',
        title: 'Horario no disponible',
        text: `Debes agendar con al menos ${MIN_BOOKING_LEAD_HOURS} horas de anticipación. Elige un horario más adelante.`,
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await runBooking();

      if (result.success) {
        if (onComplete) onComplete(result);
        return;
      }

      // Gate V2: el backend pide verificar teléfono antes de bookear.
      // Abrimos el modal de WhatsApp y guardamos el ref del booking que se
      // debe reintentar al terminar la verificación.
      if (result.phoneNotVerified) {
        pendingBookingRef.current = true;
        setShowWhatsappGate(true);
        return;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error al agendar',
        text: result.error || 'No se pudo crear la consulta. Intenta de nuevo.',
        confirmButtonColor: '#786ED9'
      });
    } catch (err) {
      console.error('[IncludedConsultationConfirm] error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error inesperado. Intenta de nuevo.',
        confirmButtonColor: '#786ED9'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Tras verificar el teléfono → reintentar el booking automáticamente.
  const handlePhoneVerified = async () => {
    setShowWhatsappGate(false);
    if (!pendingBookingRef.current) return;
    pendingBookingRef.current = false;

    setSubmitting(true);
    try {
      const result = await runBooking();
      if (result.success) {
        if (onComplete) onComplete(result);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al agendar',
          text: result.error || 'No se pudo crear la consulta. Intenta de nuevo.',
          confirmButtonColor: '#786ED9'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneCancel = () => {
    setShowWhatsappGate(false);
    pendingBookingRef.current = false;
  };

  const canSubmit = reason.trim() && !submitting;

  return (
    <div className="max-w-lg mx-auto px-4 py-4 md:py-8 min-h-screen">
      {/* Back */}
      <button
        onClick={onBack}
        className="press-scale mb-6 text-daisies flex items-center gap-2 hover:gap-3 transition-all text-sm font-medium"
      >
        <ArrowLeft size={18} /> Volver
      </button>

      {/* Header */}
      <div className="text-center mb-6 animate-fadeInUp">
        <div className="mx-auto w-16 h-16 rounded-full bg-daisies flex items-center justify-center mb-4">
          <Stethoscope size={28} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2 tracking-tight">
          {isOnDemand ? 'Solicitar consulta' : 'Agendar consulta incluida'}
        </h2>
        <p className="text-text-secondary text-sm">
          {isOnDemand
            ? `${doctorName} debe aceptar tu solicitud antes de quedar agendada`
            : 'Tu membresía incluye esta consulta sin costo adicional'}
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-card p-5 md:p-6 mb-4 animate-fadeInUp-1">
        <h3 className="ds-caption text-text-secondary uppercase mb-4">Resumen de tu cita</h3>

        <div className="space-y-4">
          {/* Specialty */}
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-tint text-brand shrink-0">
              <Stethoscope size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="ds-caption text-text-secondary">Especialidad</p>
              <p className="font-semibold text-text-primary">{specialtyName}</p>
            </div>
          </div>

          {/* Doctor */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
              <User size={16} className="text-daisies" />
            </div>
            <div>
              <p className="ds-caption text-text-secondary">Profesional</p>
              <p className="font-semibold text-text-primary">{doctorName}</p>
            </div>
          </div>

          {/* Date */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
              <Calendar size={16} className="text-daisies" />
            </div>
            <div>
              <p className="ds-caption text-text-secondary">Fecha</p>
              <p className="font-semibold text-text-primary capitalize">{formattedDate}</p>
            </div>
          </div>

          {/* Time */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
              <Clock size={16} className="text-daisies" />
            </div>
            <div>
              <p className="ds-caption text-text-secondary">Hora</p>
              <p className="font-semibold text-text-primary">{selectedTime || selectedSlot?.init}</p>
            </div>
          </div>
        </div>

        {/* Badge: bajo confirmación (on-demand) o incluida con membresía */}
        {isOnDemand ? (
          <div className="mt-5 p-3 rounded-input bg-amber-pill/20 flex items-center gap-2">
            <Clock size={18} className="text-amber-deep flex-shrink-0" />
            <p className="text-sm text-amber-deep font-medium">
              Bajo confirmación: la doctora tiene 24 h para aceptar o rechazar
            </p>
          </div>
        ) : (
          <div className="mt-5 p-3 rounded-input bg-daisies-50 flex items-center gap-2">
            <Crown size={18} className="text-daisies flex-shrink-0" />
            <p className="text-sm text-daisies font-medium">Incluida con tu membresía, sin costo</p>
          </div>
        )}
      </div>

      {/* Reason */}
      <div className="bg-white rounded-card p-5 md:p-6 mb-6 animate-fadeInUp-2">
        <label className="block text-sm font-semibold text-text-primary mb-2">
          Motivo de consulta *
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Describe brevemente el motivo de tu consulta..."
          rows={3}
          className="w-full p-4 rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary resize-none transition-all"
        />
      </div>

      {/* Confirm button */}
      <button
        onClick={handleConfirm}
        disabled={!canSubmit}
        className={`press-scale w-full py-4 rounded-full text-white font-semibold text-lg transition-all ${
          canSubmit
            ? 'bg-daisies hover:bg-daisies-hover'
            : 'bg-daisies/40 cursor-not-allowed'
        }`}
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <Loader size={20} className="animate-spin" /> {isOnDemand ? 'Enviando solicitud...' : 'Agendando...'}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <CheckCircle size={20} /> {isOnDemand ? 'Enviar solicitud' : 'Agendar Consulta'}
          </span>
        )}
      </button>

      {/* Gate V2 — Verificación de WhatsApp justo antes de bloquear el slot */}
      {showWhatsappGate && (
        <WhatsappVerificationModal
          userId={loggedInUser?.daisUserId || loggedInUser?.userId}
          authToken={loggedInUser?.authToken}
          phone={loggedInUser?.telefono || loggedInUser?.phone}
          onVerified={handlePhoneVerified}
          onCancel={handlePhoneCancel}
        />
      )}
    </div>
  );
};

export default IncludedConsultationConfirm;
